import React from "react";

import NewsCardCover from "../news_card_cover";
import Skeleton from "../skeleton";

import "./index.less";

/**
 * 完整资讯卡片
 *
 * 结构：封面（复用 NewsCardCover）→ 资讯信息。
 * 封面和信息垂直排列；封面宽度撑满卡片，资讯信息按设备切换 padding / 标题字号。
 *
 * 设备判定沿用官网现有规则：
 * - Web：默认值
 * - Pad 横屏：祖先节点带 .ow-pad-land
 * - Pad 竖屏：祖先节点带 .ow-pad-port
 * - 移动端：≤767px 媒体查询
 *
 * @param {string} brandName 封面品牌名称，默认 BUJUE
 * @param {string} typeName 封面类型名称
 * @param {string} title 资讯标题（封面和信息区共用）
 * @param {string} coverUrl 封面图地址；有值时封面只显示图，不显示标题
 * @param {boolean} pinned 是否置顶（封面上显示「置顶」）
 * @param {boolean} listMode 封面列表模式：true 显示品牌/分类和置顶；默认 true
 * @param {string} category 资讯分类 tag；为空时不渲染
 * @param {string} summary 资讯摘要，最多 3 行（不足 3 行也预留 3 行高度）
 * @param {string} teamName 资讯团队名称
 * @param {string} publishDate 资讯发表日期（字符串原样展示）
 * @param {boolean} skeleton 骨架屏模式；尺寸与真实卡片一致，只渲染骨块
 */
export default function NewsCard({
  brandName = "BUJUE",
  typeName = "",
  title = "",
  coverUrl = "",
  pinned = false,
  listMode = true,
  category = "",
  summary = "",
  teamName = "",
  publishDate = "",
  skeleton = false,
  className,
  ...rest
}) {
  if (skeleton) {
    const skeletonClassName = [
      "news-card",
      "news-card--skeleton",
      className || "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={skeletonClassName} aria-hidden="true">
        <div className="news-card__skeleton-cover">
          <Skeleton
            className="news-card__skeleton-cover-bg"
            width="100%"
            height="100%"
            borderRadius={0}
          />
          <Skeleton
            className="news-card__skeleton-cover-brand"
            width={72}
            height={20}
            borderRadius={0}
            backgroundColor="#ededed"
          />
        </div>

        <div className="news-card__info">
          <div className="news-card__tag-row">
            <Skeleton
              width={64}
              height={19}
              borderRadius={4}
              backgroundColor="#f1f1f1"
            />
          </div>

          <div className="news-card__skeleton-title">
            <Skeleton width="100%" height="100%" borderRadius={0} />
          </div>

          <div className="news-card__skeleton-summary">
            <Skeleton width="100%" height="100%" borderRadius={0} />
          </div>

          <div className="news-card__meta">
            <Skeleton width={72} height={20} borderRadius={0} />
            <Skeleton
              className="news-card__skeleton-date"
              width={72}
              height={20}
              borderRadius={0}
            />
          </div>
        </div>
      </div>
    );
  }

  const titleText = String(title || "");
  const categoryText = String(category || "").trim();
  const summaryText = String(summary || "");
  const teamText = String(teamName || "");
  const dateText = String(publishDate || "");

  const rootClassName = ["news-card", className || ""].filter(Boolean).join(" ");

  return (
    <div className={rootClassName} {...rest}>
      <NewsCardCover
        listMode={listMode}
        brandName={brandName}
        typeName={typeName}
        title={titleText}
        coverUrl={coverUrl}
        pinned={pinned}
      />

      <div className="news-card__info">
        {categoryText ? (
          <div className="news-card__tag-row">
            <span className="news-card__tag">{categoryText}</span>
          </div>
        ) : null}

        <div className="news-card__title" title={titleText}>
          {titleText}
        </div>

        {/* 摘要固定占 3 行高度；内容不足 3 行也保留，保证卡片高度一致 */}
        <div className="news-card__summary">{summaryText}</div>

        <div className="news-card__meta">
          <span className="news-card__team" title={teamText}>
            {teamText}
          </span>
          <span className="news-card__date">{dateText}</span>
        </div>
      </div>
    </div>
  );
}
